import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  type InfiniteData,
  type QueryClient,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/vue-query";
import {
  DateSortAttribute,
  DateSortDirection,
  EventsApi,
  EventsOverviewApi,
  type EventsOverviewList,
  FilterApi,
} from "mws-ts-v2-sdk";
import { unref } from "vue";

import { getStaleOnServer } from "~/constants/misc";
import type PlatformModule from "~/store/PlatformModule";
import type { ExtendedEventsOverviewList } from "~/types/Catalog";
import { CategoryTabs } from "~/types/Category";
import { getUniversalAxios } from "~/utils/axios/getUniversalAxios";

const getEventsApi = () => {
  return new EventsApi(
    undefined,
    useRuntimeConfig().public.catalogServiceUrl,
    getUniversalAxios(),
  );
};

const getEventsOverviewApi = () => {
  return new EventsOverviewApi(
    undefined,
    useRuntimeConfig().public.catalogServiceUrl,
    getUniversalAxios(),
  );
};

const getEventsFilterApi = () => {
  return new FilterApi(
    undefined,
    useRuntimeConfig().public.catalogServiceUrl,
    getUniversalAxios(),
  );
};

export const eventQueryKeys = createQueryKeys("events", {
  details: (
    eventIdentifier: MaybeRef<string>,
    platformId: MaybeRef<string>,
  ) => ({
    queryKey: [eventIdentifier],
    queryFn: async () => {
      const response = await getEventsApi().v2EventsIdGet({
        id: unref(eventIdentifier),
        xPlatformKey: unref(platformId),
      });

      return response.data;
    },
  }),
  filters: (platformId: MaybeRef<string>, searchTerm: MaybeRef<string>) => ({
    queryKey: [searchTerm],
    queryFn: async () => {
      const response = await getEventsFilterApi().v2FilterEventsGet({
        searchTerm: unref(searchTerm),
        xPlatformKey: unref(platformId),
      });

      return response.data;
    },
  }),
});

export const useEventQuery = (
  eventIdentifier: MaybeRefOrGetter<string | null | undefined>,
  params: {
    enabled?: MaybeRef<boolean>;
    suspense?: boolean;
    redirectNotFound?: boolean;
  } = { enabled: true },
) => {
  const platformStore = usePlatformStore();
  const platformId = computed(
    () => platformStore.getPlatformConfiguration.platformId,
  );

  const queryIsEnabled = computed(() => {
    return unref(params.enabled) && Boolean(unref(eventIdentifier));
  });

  const queryEventIdentifier = computed(() => toValue(eventIdentifier) ?? "");

  return useQuery({
    ...eventQueryKeys.details(queryEventIdentifier, platformId),
    enabled: queryIsEnabled,
    suspense: params.suspense,
    staleTime: params.suspense ? getStaleOnServer : undefined,
    ...(params.redirectNotFound && { ...notFoundQueryOptions }),
  });
};

type EventsOverviewInfiniteQueryKey = Readonly<
  [
    "events",
    "overview",
    "infiniteList",
    {
      onHomepage: MaybeRef<boolean>;
      liveStatus: MaybeRef<CategoryTabs | undefined>;
      categoryId: MaybeRef<string | undefined>;
    },
    {
      filters: MaybeRef<string[]>;
      pageSize: MaybeRef<number>;
      sortBy: MaybeRef<DateSortAttribute | undefined>;
      sortDirection: MaybeRef<DateSortDirection | undefined>;
    },
  ]
>;

export const useEventsOverviewInfiniteQuery = (
  data: {
    onHomepage?: MaybeRefOrGetter<boolean>;
    categoryId?: MaybeRefOrGetter<string>;
    numberOfProducts?: MaybeRefOrGetter<number>;
    liveStatus: MaybeRefOrGetter<CategoryTabs | undefined>;
    filters?: MaybeRefOrGetter<string[]>;
    sort?: {
      by: MaybeRefOrGetter<DateSortAttribute | undefined>;
      direction: MaybeRefOrGetter<DateSortDirection | undefined>;
    };
    pageSize: MaybeRefOrGetter<number>;
  },
  options?: {
    suspenseWithHydration?: boolean;
  },
) => {
  const onHomepage = computed(() => toValue(data.onHomepage) ?? false);
  const liveStatus = computed(() => toValue(data.liveStatus));
  const categoryId = computed(() => toValue(data.categoryId));
  const pageSize = computed(() => toValue(data.pageSize));
  const numberOfProducts = computed(() => toValue(data.numberOfProducts) ?? 25);
  const filters = computed(() => toValue(data.filters) ?? []);
  const sortBy = computed(() => toValue(data.sort?.by));
  const sortDirection = computed(() => toValue(data.sort?.direction));

  const { activeCurrency } = useActiveCurrency();
  const activeCurrencyCode = computed(
    () => activeCurrency.value.isoCurrencyCode,
  );

  const platformStore = usePlatformStore();
  const platformId = computed(
    () => platformStore.getPlatformConfiguration.platformId,
  );

  const queryKey: EventsOverviewInfiniteQueryKey = [
    "events",
    "overview",
    "infiniteList",
    {
      onHomepage,
      liveStatus,
      categoryId,
    },
    {
      filters,
      sortBy,
      sortDirection,
      pageSize,
    },
  ];

  const queryClient = useQueryClient();

  watch(activeCurrencyCode, (newCurrencyCode, oldCurrencyCode) => {
    // invalidate the query when the currency changes
    // don't add currency to the query key to avoid clearing the data
    if (newCurrencyCode !== oldCurrencyCode) {
      queryClient.invalidateQueries({
        queryKey,
      });
    }
  });

  const query = useInfiniteQuery<
    EventsOverviewList,
    Error,
    InfiniteData<ExtendedEventsOverviewList>,
    EventsOverviewInfiniteQueryKey,
    number
  >({
    suspense: options?.suspenseWithHydration,
    staleTime: options?.suspenseWithHydration ? getStaleOnServer : undefined,
    enabled: () => Boolean(liveStatus.value),
    queryKey,
    queryFn: async ({ pageParam }) => {
      const parentCategoryIdValue = unref(categoryId);
      const liveStatusValue = unref(liveStatus);
      const filtersValue = unref(filters);

      let filterCategoryIds: string[] = [];
      if (filtersValue.length > 0) {
        filterCategoryIds = [
          // Make array unique
          ...new Set(filtersValue),
        ];
      }

      const response = await getEventsOverviewApi().v2EventsOverviewGet({
        xPlatformKey: unref(platformId),
        currency: unref(activeCurrencyCode),
        onHomepage: unref(onHomepage),
        ...(liveStatusValue && {
          liveStatus: liveStatusValue,
        }),
        maxNumberOfProducts: unref(numberOfProducts),
        offset: pageParam,
        pageSize: unref(pageSize),
        ...(parentCategoryIdValue && {
          parentCategoryId: parentCategoryIdValue,
        }),
        ...(filterCategoryIds.length > 0 && {
          categoryIds: filterCategoryIds,
        }),
        sortBy: unref(sortBy),
        sortDirection: unref(sortDirection),
      });

      return response.data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextPageOffset = lastPage.offset + unref(pageSize);
      if (nextPageOffset >= lastPage.totalCount) return undefined; // no next page

      return nextPageOffset;
    },
    placeholderData: (data, queryOptions) => {
      const oldLiveStatus = queryOptions?.queryKey[3].liveStatus;
      const newLiveStatus = unref(liveStatus);

      // Don't paste over data when switching tabs so users see the loading state and it's clear
      // something is changing. If they only change a filter we want to paste over and hide
      // the loading state
      if (oldLiveStatus === newLiveStatus) {
        return data;
      }
    },
    select: (results) => {
      return {
        ...results,
        pages: results.pages.map((page) => ({
          ...page,
          results: page.results.map((event) => ({
            ...event,
            isFinished: liveStatus.value === CategoryTabs.Historical,
          })),
        })),
      };
    },
  });

  if (options?.suspenseWithHydration) {
    onServerPrefetch(async () => await query.suspense());
  }

  return query;
};

export const eventsOverviewPageSizes = {
  allEvents: 20,
  category: 8,
};

export const usePrefetchEventsOverviewInfiniteQuery = () => {
  const queryClient = useQueryClient();

  const platformStore = usePlatformStore();
  const platformId = computed(
    () => platformStore.getPlatformConfiguration.platformId,
  );

  const { activeCurrency } = useActiveCurrency();
  const activeCurrencyCode = computed(
    () => activeCurrency.value.isoCurrencyCode,
  );

  const toast = useToast();
  const { t } = useI18n();

  return async (options: {
    onHomepage?: MaybeRefOrGetter<boolean>;
    categoryId?: MaybeRefOrGetter<string>;
    liveStatus: MaybeRefOrGetter<CategoryTabs | undefined>;
    pageSize: number;
  }) => {
    const onHomepage = toValue(options.onHomepage) ?? false;
    const liveStatus = toValue(options.liveStatus);
    const categoryId = toValue(options.categoryId);
    const pageSize = options.pageSize;

    return queryClient.fetchInfiniteQuery({
      queryKey: [
        "events",
        "overview",
        "infiniteList",
        {
          activeCurrency: unref(activeCurrencyCode),
          liveStatus,
          categoryId,
          onHomepage,
        },
      ],
      queryFn: async ({ pageParam }) => {
        const categoryIdValue = unref(categoryId);

        try {
          const response = await getEventsOverviewApi().v2EventsOverviewGet({
            xPlatformKey: unref(platformId),
            currency: unref(activeCurrencyCode),
            onHomepage,
            liveStatus,
            offset: pageParam,
            pageSize,
            ...(categoryIdValue && {
              categoryIds: [categoryIdValue],
            }),
          });

          return response.data;
        } catch {
          toast.error(t("errors.loadingEvents"));
        }
      },
      initialPageParam: 0,
    });
  };
};

export const useRefetchEventWithProductsInfiniteQuery = () => {
  const queryClient = useQueryClient();

  const { activeCurrency } = useActiveCurrency();
  const activeCurrencyCode = computed(
    () => activeCurrency.value.isoCurrencyCode,
  );

  const platformStore = usePlatformStore();
  const platformId = computed(
    () => platformStore.getPlatformConfiguration.platformId,
  );

  const toast = useToast();

  const { t } = useI18n();

  return async (eventId: string) => {
    try {
      const updatedEventInfiniteList =
        await getEventsOverviewApi().v2EventsOverviewGet({
          xPlatformKey: unref(platformId),
          currency: unref(activeCurrencyCode),
          liveStatus: "started",
          eventId,
          pageSize: 1,
        });

      const updatedEvent = updatedEventInfiniteList.data.results[0];

      if (updatedEvent) {
        const eventsWithProductsQueryCaches = queryClient.getQueriesData<
          InfiniteData<EventsOverviewList>
        >({
          queryKey: [
            "events",
            "overview",
            "infiniteList",
            { activeCurrency: unref(activeCurrencyCode) },
          ],
        });

        eventsWithProductsQueryCaches.forEach(([queryKey, queryCache]) => {
          if (queryCache) {
            const updatedQueryCache = {
              ...queryCache,
              pages: queryCache.pages.map((page) => ({
                ...page,
                results: page.results.map((event) => {
                  return event.id === eventId ? updatedEvent : event;
                }),
              })),
            };

            queryClient.setQueryData<InfiniteData<EventsOverviewList>>(
              queryKey,
              updatedQueryCache,
            );
          }
        });
      }
    } catch {
      toast.error(t("event.productsError"));
    }
  };
};

export const prefetchEvent = (
  eventIdentifier: MaybeRef<string>,
  queryClient: QueryClient,
  platformStore: PlatformModule,
) => {
  const platformId = platformStore.getPlatformConfiguration.platformId;

  return queryClient.prefetchQuery({
    ...eventQueryKeys.details(unref(eventIdentifier), platformId),
    staleTime: 60000,
  });
};
